import type { Locale } from '@mfw/translations';

import { getLocalizedNameByLocale } from '@mfw/components';
import { config } from '@mfw/translations';

import type {
  ApiCourseTranslation,
  UiCreateCourse,
  ApiCourse,
  UiCourse,
} from '../../schemas/courses.schema';
import type { UiEducationTranslation } from '../../schemas/modules.schema';

import { DEFAULT_CURRENCY } from '../../config/constants';

function nameFromTranslations(
  locale: Locale,
  translations?: ApiCourseTranslation[],
): string {
  if (!translations?.length) return '';
  return (
    translations.find((t) => t.locale === locale)?.name ||
    translations.find((t) => t.locale === config.defaultLocale)?.name ||
    translations[0]?.name ||
    ''
  );
}

function mapTranslations(
  translations: ApiCourseTranslation[],
): UiEducationTranslation[] {
  return (translations ?? []).map((t) => ({
    description: t.description ?? '',
    locale: [
      { label: getLocalizedNameByLocale(t.locale as Locale), value: t.locale },
    ],
    name: t.name,
  }));
}

export function mapCourseFromApi(course: ApiCourse, locale: Locale): UiCourse {
  return {
    id: course.id,
    authorId: course.author?.id,
    previewOnly: course.previewOnly,
    authorName: course.author?.name,
    coverSignedUrl: course.coverSignedUrl,
    coverSource: course.coverSource,
    coverType: course.coverType,
    createdAt: course.createdAt,
    documentSignedUrl: course.documentSignedUrl,
    documentSource: course.documentSource,
    documentType: course.documentType,
    isDeleted: course.isDeleted ?? false,
    isMarketplace:
      course.configuration?.marketplace?.enabled ??
      course.isMarketplace ??
      false,
    isOrg: course.configuration?.isOrg ?? course.isOrg ?? true,
    marketplaceCurrency:
      course.configuration?.marketplace?.currency ?? DEFAULT_CURRENCY,
    marketplaceId: course.configuration?.marketplace?.id ?? null,
    marketplaceInLibrary: course.configuration?.marketplace?.inLibrary ?? false,
    marketplaceRestorable:
      course.configuration?.marketplace?.restorable ?? false,
    marketplaceLibraryId: course.configuration?.marketplace?.libraryId ?? null,
    marketplacePrice: course.configuration?.marketplace?.price ?? 0,
    modules: (course.courseModules ?? [])
      .map((cm) => ({
        moduleId: cm.moduleId,
        name: nameFromTranslations(locale, cm.module?.translations),
        position: cm.position,
      }))
      .sort((a, b) => a.position - b.position),
    organizationId: course.organizationId,
    sequentialUnlock: course.sequentialUnlock,
    stats: course.stats,
    translations: mapTranslations(course.translations),
    updatedAt: course.updatedAt,
  };
}

export function createCourseApiArgs(course: UiCreateCourse) {
  return {
    configuration: {
      isOrg: course.isOrg ?? true,
      marketplace: {
        currency: course.marketplaceCurrency ?? DEFAULT_CURRENCY,
        enabled: Boolean(course.isMarketplace),
        price: course.marketplacePrice ?? 0,
      },
    },
    coverSource: course.coverSource ?? null,
    coverType: course.coverType ?? null,
    documentSource: course.documentSource ?? null,
    documentType: course.documentType ?? null,
    modules: course.modules,
    sequentialUnlock: course.sequentialUnlock ?? true,
    translations: (course.translations ?? []).map((t) => ({
      description: t.description,
      locale: t.locale?.[0]?.value,
      name: t.name,
    })),
  };
}
